import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import FilterPanel from '../../components/FilterPanel';
import { DataGrid } from '@mui/x-data-grid';
const yn = (value) => (value ? 'כן' : 'לא');
const categoryLabel = (item) =>
  item?.displayNames?.he || item?.displayName || item?.displayNames?.en || item?.systemName;
export default function OsmImportPage({ request }) {
  const [countries, setCountries] = useState([]),
    [country, setCountry] = useState(null);
  const [cities, setCities] = useState([]),
    [city, setCity] = useState(null);
  const [categories, setCategories] = useState([]),
    [chosenCategories, setChosenCategories] = useState([]);
  const [rows, setRows] = useState([]),
    [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    existing: '',
    inside: '',
    polygon: '',
    hours: '',
  });
  useEffect(() => {
    Promise.all([
      request('/bo/osm-import/countries?locale=he'),
      request('/bo/osm-import/categories'),
    ])
      .then(([allCountries, allCategories]) => {
        setCountries(allCountries);
        setCategories(allCategories);
        setChosenCategories(allCategories.map((item) => item.systemName));
      })
      .catch((e) => setError(e.message));
  }, [request]);
  const chooseCountry = async (_, value) => {
    setCountry(value);
    setCity(null);
    setCities([]);
    setRows([]);
    if (!value) return;
    setBusy(true);
    setError('');
    try {
      setCities(
        await request(`/bo/osm-import/cities?countryCode=${encodeURIComponent(value.code)}`),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const preview = async () => {
    if (!country || !city || !chosenCategories.length)
      return setError('יש לבחור מדינה, עיר ולפחות קטגוריה אחת');
    setBusy(true);
    setError('');
    setSummary(null);
    try {
      const result = await request('/bo/osm-import/preview', {
        method: 'POST',
        body: JSON.stringify({
          countryCode: country.code,
          countryNameHe: country.nameHe,
          countryNameEn: country.nameEn,
          city,
          cityNameHe: city.nameHe,
          cityNameEn: city.nameEn,
          categories: chosenCategories,
        }),
      });
      setRows(result);
      setSelected(result.filter((item) => item.selected).map((item) => item.key));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const runImport = async () => {
    const items = rows.filter((item) => selected.includes(item.key));
    if (!items.length) return setError('לא נבחרו מקומות לייבוא');
    setBusy(true);
    setError('');
    try {
      const result = await request('/bo/osm-import/import', {
        method: 'POST',
        body: JSON.stringify({
          items,
        }),
      });
      setSummary(result);
      setSelected([]);
      await preview();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const visible = useMemo(
    () =>
      rows.filter((item) => {
        const text = `${item.name?.he || ''} ${item.name?.en || ''}`.toLowerCase();
        return (
          (!filters.search || text.includes(filters.search.toLowerCase())) &&
          (!filters.category || item.categories.includes(filters.category)) &&
          (!filters.existing || String(!!item.existingPlace) === filters.existing) &&
          (!filters.inside || String(!!item.containment) === filters.inside) &&
          (!filters.polygon || String(item.hasFootprint) === filters.polygon) &&
          (!filters.hours || String(item.hasOpeningHours) === filters.hours)
        );
      }),
    [rows, filters],
  );
  const columns = [
    ['nameHe', 'שם בעברית', 145, (_, row) => row.name?.he || ''],
    ['nameEn', 'שם באנגלית', 145, (_, row) => row.name?.en || ''],
    ['categories', 'קטגוריות', 170, (_, row) => row.categories.join(', ')],
    ['cityHe', 'עיר בעברית', 115, (_, row) => row.city?.he || ''],
    ['cityEn', 'עיר באנגלית', 115, (_, row) => row.city?.en || ''],
    ['countryHe', 'ארץ בעברית', 115, (_, row) => row.country?.he || ''],
    ['countryEn', 'ארץ באנגלית', 115, (_, row) => row.country?.en || ''],
    ['inside', 'בתוך מתחם', 105, (_, row) => yn(row.containment)],
    ['container', 'מקום מכיל', 140, (_, row) => row.containment?.parentName || ''],
    ['level', 'קומה', 70, (_, row) => row.level || ''],
    ['location', 'מיקום', 75, (_, row) => yn(row.hasLocation)],
    ['polygon', 'פוליגון', 80, (_, row) => yn(row.hasFootprint)],
    ['hours', 'שעות', 70, (_, row) => yn(row.hasOpeningHours)],
    ['existing', 'קיים במאגר', 105, (_, row) => yn(row.existingPlace)],
    ['containerExisting', 'המתחם קיים', 110, (_, row) => yn(row.containment?.existingPlace)],
    ['warnings', 'אזהרות', 250, (_, row) => row.warnings.join(', ')],
  ].map(([field, headerName, width, valueGetter]) => ({
    field,
    headerName,
    width,
    valueGetter,
  }));
  const setFilter = (key) => (event) =>
    setFilters((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  const boolFilter = (key, title) => (
    <TextField
      select
      size="small"
      label={title}
      value={filters[key]}
      onChange={setFilter(key)}
      sx={{
        minWidth: 125,
      }}
    >
      <MenuItem value="">הכול</MenuItem>
      <MenuItem value="true">כן</MenuItem>
      <MenuItem value="false">לא</MenuItem>
    </TextField>
  );
  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="h4">ייבוא עסקים מ־OpenStreetMap</Typography>
        <Typography color="text.secondary">הנתונים נשמרים רק לאחר Preview ואישור מנהל</Typography>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {summary && (
        <Alert severity="success">
          יובאו {summary.imported}, דולגו {summary.skippedExisting}, נכשלו {summary.failed}, מתחמים
          חדשים {summary.complexesCreated}
        </Alert>
      )}
      <Paper
        sx={{
          p: 2,
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={2}
          alignItems="center"
        >
          <Autocomplete
            options={countries}
            getOptionLabel={(item) => item.name || ''}
            value={country}
            onChange={chooseCountry}
            renderInput={(params) => <TextField {...params} label="מדינה" />}
            sx={{
              minWidth: 220,
            }}
          />
          <Autocomplete
            options={cities}
            getOptionLabel={(item) => item.nameHe || item.nameEn || ''}
            value={city}
            onChange={(_, value) => setCity(value)}
            renderInput={(params) => <TextField {...params} label="עיר" />}
            sx={{
              minWidth: 230,
            }}
          />
          <Button
            variant="contained"
            onClick={preview}
            disabled={busy || !country || !city || !chosenCategories.length}
          >
            {busy ? <CircularProgress size={22} /> : 'משוך מקומות מ־OpenStreetMap'}
          </Button>
        </Stack>
        <Stack
          direction="row"
          flexWrap="wrap"
          sx={{
            mt: 2,
          }}
        >
          <Button
            size="small"
            onClick={() => setChosenCategories(categories.map((item) => item.systemName))}
          >
            בחירת הכול
          </Button>
          <Button size="small" onClick={() => setChosenCategories([])}>
            ביטול הכול
          </Button>
          {categories.map((item) => (
            <FormControlLabel
              key={item.systemName}
              control={
                <Checkbox
                  checked={chosenCategories.includes(item.systemName)}
                  onChange={(e) =>
                    setChosenCategories((current) =>
                      e.target.checked
                        ? [...current, item.systemName]
                        : current.filter((name) => name !== item.systemName),
                    )
                  }
                />
              }
              label={categoryLabel(item)}
            />
          ))}
        </Stack>
      </Paper>
      {!!rows.length && (
        <>
          <Paper
            sx={{
              p: 2,
            }}
          >
            <FilterPanel>
              <TextField
                size="small"
                label="חיפוש בטבלה"
                value={filters.search}
                onChange={setFilter('search')}
              />
              <TextField
                select
                size="small"
                label="קטגוריה"
                value={filters.category}
                onChange={setFilter('category')}
                sx={{
                  minWidth: 140,
                }}
              >
                <MenuItem value="">הכול</MenuItem>
                {categories.map((item) => (
                  <MenuItem key={item.systemName} value={item.systemName}>
                    {categoryLabel(item)}
                  </MenuItem>
                ))}
              </TextField>
              {boolFilter('existing', 'קיים במאגר')}
              {boolFilter('inside', 'בתוך מתחם')}
              {boolFilter('polygon', 'פוליגון')}
              {boolFilter('hours', 'שעות')}
            </FilterPanel>
          </Paper>
          <Paper
            sx={{
              height: 600,
            }}
          >
            <DataGrid
              rows={visible}
              getRowId={(row) => row.key}
              columns={columns}
              checkboxSelection
              rowSelectionModel={{
                type: 'include',
                ids: new Set(selected),
              }}
              onRowSelectionModelChange={(model) => setSelected([...model.ids])}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 50,
                  },
                },
              }}
              disableRowSelectionOnClick
            />
          </Paper>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() =>
                setSelected(
                  visible
                    .filter((item) => item.valid && !item.existingPlace)
                    .map((item) => item.key),
                )
              }
            >
              בחירת הכול התקין
            </Button>
            <Button onClick={() => setSelected([])}>ביטול הכול</Button>
            <Button
              variant="contained"
              color="success"
              disabled={busy || !selected.length}
              onClick={runImport}
            >
              ייבא מקומות נבחרים ({selected.length})
            </Button>
          </Stack>
        </>
      )}
      <Typography variant="caption" color="text.secondary">
        © OpenStreetMap contributors
      </Typography>
    </Stack>
  );
}
